import React, { useState, useEffect, useRef } from "react";
import {
    Card,
    Table,
    Tag,
    Badge,
    Select,
    DatePicker,
    Input,
    Space,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import Header from "../../components/Header/header";
import styles from './audit.module.css'
import * as Action from "../../store/slices/audit/audit"
import * as UserAction from "../../store/slices/user/user"
import { connect } from "react-redux";
import dayjs from "dayjs";
import { message, Button } from "antd";
import { useNavigate } from 'react-router-dom';
import { DownloadOutlined } from '@ant-design/icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const { RangePicker } = DatePicker;

interface Api {
    getAudits: (payload: any) => unknown;
    Audit_Red: any;
    User_Red: any;
    getAllUser: (payload: any) => void;
}

interface UserActivityLog {
    id: number;
    timestamp: string;
    userName: string;
    email: string;
    role: string;
    module: string;
    action: string;
    actionName: string;
    result: "SUCCESS" | "FAILED";
    ipAddress: string;
    affectedUser: string;
}

const actionColor: Record<string, string> = {
    LOGIN: "blue",
    LOGOUT: "default",
    CREATE: "green",
    UPDATE: "cyan",
    DELETE: "red",
    ACCESS: "purple",
};

const UserActivityAuditLog: React.FC<Api> = ({ getAudits, Audit_Red, User_Red, getAllUser }) => {
    const navigate = useNavigate();
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [searchInput, setSearchInput] = useState("");
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().subtract(30, "day"),
        dayjs(),
    ]);
    const [userId, setUserId] = useState<number | null>(null);

    // Debounce ref
    const debounceRef = useRef<any>(null);

    useEffect(() => {
        getAllUser({ page: 0, size: 1000 });
    }, []);

    const userOptions = User_Red?.data?.content?.map((user: any) => ({
        label: user.userName || user.name,
        value: user.userId,
    })) || [];

    // Core fetch function — takes all params directly, no stale closure
    const doFetch = (
        pg: number,
        sz: number,
        dr: [dayjs.Dayjs, dayjs.Dayjs],
        uid: number | null,
        search: string
    ) => {
        getAudits({
            page: pg,
            size: sz,
            fromDate: dr[0].format("YYYY-MM-DD"),
            toDate: dr[1].format("YYYY-MM-DD"),
            userId: uid ?? 0,
            username: search.trim(),
            actionName: "",
            module: "",
            ipAddress: "",
            isException: false,
            sortBy: "requestAt",
            direction: "DESC",
        });
    };

    // Initial load
    useEffect(() => {
        doFetch(page, pageSize, dateRange, userId, "");
    }, []);

    const pageData = Audit_Red?.data?.[0]?.res?.data;

    useEffect(() => {
        if (Audit_Red?.dataSingle?.[0]?.res?.status === '1004') {
            message.error(Audit_Red.dataSingle[0].res.message);
            localStorage.clear();
            navigate('/');
        } else if (Audit_Red?.dataSingle?.[0]?.res?.status === '1005') {
            message.warning(Audit_Red.dataSingle[0].res.message);
        }
    }, [Audit_Red, navigate]);

    // Helper: extract affected userName
    const getAffectedUser = (item: any): string => {
        if (item.module !== 'User Management') return '-';
        try {
            const action = item.actionName?.toUpperCase() || '';
            const newData = item.newData ? JSON.parse(item.newData) : null;
            const oldData = item.oldData ? JSON.parse(item.oldData) : null;
            if (action === 'CREATE_USER') return newData?.userName || '-';
            if (action === 'UPDATE_USER') {
                const o = oldData?.userName || '-';
                const n = newData?.userName || '-';
                return o !== n ? `${o} → ${n}` : n;
            }
            if (['DELETE_USER', 'APPROVE_USER', 'REJECT_USER', 'UNLOCK_USER'].includes(action))
                return oldData?.userName || '-';
            return newData?.userName || oldData?.userName || '-';
        } catch {
            return '-';
        }
    };

    const allTableData: UserActivityLog[] =
        pageData?.content?.map((item: any) => ({
            id: item.activityLogId,
            timestamp: item.requestAt,
            userName: item.username || "-",
            email: "-",
            role: "-",
            module: item.module || item.uri || "-",
            action: item.actionDescription || "-",
            actionName: item.actionName || "-",
            result: (item.httpStatus >= 200 && item.httpStatus < 300) ? "SUCCESS" : "FAILED",
            ipAddress: item.ipAddress || "-",
            affectedUser: getAffectedUser(item),
        })) ?? [];

    // Frontend filter for module & action (username is server-side)
    const searchLower = searchInput.trim().toLowerCase();
    const tableData = searchLower
        ? allTableData.filter((row) =>
              row.userName.toLowerCase().includes(searchLower) ||
              row.module.toLowerCase().includes(searchLower) ||
              row.action.toLowerCase().includes(searchLower)
          )
        : allTableData;

    const columns: ColumnsType<UserActivityLog> = [
        {
            title: "Date & Time",
            dataIndex: "timestamp",
            key: "timestamp",
            render: (text) => (text ? dayjs(text).format("YYYY-MM-DD HH:mm:ss") : "-"),
        },
        {
            title: "User",
            key: "user",
            render: (_, record) => <strong>{record.userName}</strong>,
        },
        {
            title: "Module",
            dataIndex: "module",
        },
        {
            title: "Action",
            dataIndex: "action",
            render: (action: string, record: UserActivityLog) => (
                <span>
                    <Tag color={actionColor[action?.toUpperCase()] || "default"}>{action}</Tag>
                    {record.affectedUser !== '-' && (
                        <span style={{ fontSize: 12, color: '#5a5f7d', marginLeft: 4 }}>
                            → {record.affectedUser}
                        </span>
                    )}
                </span>
            ),
        },
        {
            title: "Result",
            dataIndex: "result",
            render: (result) => (
                <Badge status={result === "SUCCESS" ? "success" : "error"} text={result} />
            ),
        },
        {
            title: "IP Address",
            dataIndex: "ipAddress",
        },
    ];

    const handleTableChange = (pagination: any) => {
        const newPage = pagination.current - 1;
        const newSize = pagination.pageSize;
        setPage(newPage);
        setPageSize(newSize);
        doFetch(newPage, newSize, dateRange, userId, searchInput);
    };

    const handleDateChange = (dates: any) => {
        if (!dates || !dates[0] || !dates[1]) return;
        const newRange: [dayjs.Dayjs, dayjs.Dayjs] = [dates[0], dates[1]];
        setDateRange(newRange);
        setPage(0);
        doFetch(0, pageSize, newRange, userId, searchInput);
    };

    const handleUserChange = (value: number | undefined) => {
        const uid = value ?? null;
        setSelectedUserId(uid);
        setUserId(uid);
        setPage(0);
        doFetch(0, pageSize, dateRange, uid, searchInput);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setSearchInput(val);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(0);
            // When searching by module/action, fetch larger page to filter frontend
            const fetchSize = val.trim() ? 200 : pageSize;
            doFetch(0, fetchSize, dateRange, userId, val);
        }, 600);
    };

    const handleSearchClear = () => {
        setSearchInput("");
        setPage(0);
        doFetch(0, pageSize, dateRange, userId, "");
    };

    const total = pageData?.totalElements ?? 0;

    const generatePDF = async () => {
        if (total === 0) {
            message.warning('No data available to generate report');
            return;
        }
        setIsGeneratingPDF(true);
        const hideLoading = message.loading('Fetching all records for PDF...', 0);
        try {
            const selectedUser = userOptions.find((u: any) => u.value === userId);
            const token = localStorage.getItem("loginToken");
            const API_URL = process.env.REACT_APP_API_URL;

            const queryParams: Record<string, string> = {
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                username: searchInput.trim(),
                ipAddress: "",
                actionName: "",
                module: "",
                isException: "false",
                page: "0",
                size: "100000",
                sortBy: "requestAt",
                direction: "DESC",
            };
            if (userId) queryParams.userId = String(userId);

            const query = new URLSearchParams(queryParams).toString();
            const response = await fetch(`${API_URL}/api/activity/logs?${query}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            const result = await response.json();
            const fullData =
                result?.data?.content ||
                result?.data?.data?.content ||
                (Array.isArray(result?.data) ? result.data : null) ||
                result?.content ||
                (Array.isArray(result) ? result : []);

            if (!fullData || fullData.length === 0) {
                hideLoading();
                message.warning('No data retrieved for PDF');
                setIsGeneratingPDF(false);
                return;
            }

            const doc = new jsPDF();
            doc.setFontSize(18);
            doc.text('Audit Activity Report', 14, 20);
            doc.setFontSize(11);
            doc.text(`Generated: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`, 14, 30);
            doc.text(`Period: ${dateRange[0].format("YYYY-MM-DD")} to ${dateRange[1].format("YYYY-MM-DD")}`, 14, 37);
            doc.text(`User: ${selectedUser ? selectedUser.label : 'All Users'}`, 14, 44);
            doc.text(`Total Records: ${fullData.length}`, 14, 51);

            const tableColumn = ['Date & Time', 'User', 'Module', 'Action', 'Result', 'IP Address'];
            const tableRows = fullData.map((item: any) => {
                let actionText = item.actionDescription || item.actionName || '-';
                if (item.module === 'User Management') {
                    try {
                        const action = item.actionName?.toUpperCase() || '';
                        const newData = item.newData ? JSON.parse(item.newData) : null;
                        const oldData = item.oldData ? JSON.parse(item.oldData) : null;
                        let affectedUser = '-';
                        if (action === 'CREATE_USER') affectedUser = newData?.userName || '-';
                        else if (action === 'UPDATE_USER') affectedUser = `${oldData?.userName || '-'} → ${newData?.userName || '-'}`;
                        else if (['DELETE_USER', 'APPROVE_USER', 'REJECT_USER', 'UNLOCK_USER'].includes(action)) affectedUser = oldData?.userName || '-';
                        else affectedUser = newData?.userName || oldData?.userName || '-';
                        if (affectedUser !== '-') actionText = `${actionText} → ${affectedUser}`;
                    } catch { /* keep original */ }
                }
                return [
                    dayjs(item.requestAt).format('YYYY-MM-DD HH:mm:ss'),
                    item.username || '-',
                    item.uri || item.module || '-',
                    actionText,
                    item.exceptionMsg ? 'FAILED' : 'SUCCESS',
                    item.ipAddress || '-',
                ];
            });

            autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                startY: 58,
                styles: { fontSize: 8 },
                headStyles: { fillColor: [41, 128, 185] }
            });

            const fileName = `audit_report_${selectedUser ? selectedUser.label : 'all_users'}_${dayjs().format('YYYYMMDD_HHmmss')}.pdf`;
            doc.save(fileName);
            hideLoading();
            message.success(`PDF report with ${fullData.length} records downloaded successfully`);
            setIsGeneratingPDF(false);
        } catch (error) {
            hideLoading();
            message.error('Failed to generate PDF report');
            setIsGeneratingPDF(false);
        }
    };

    return (
        <>
            <Header />
            <div className={styles.container}>
                <div>
                    <h4>Activity Audit Log</h4>
                </div>
                <Card title="User Activity Audit Log">
                    <Space wrap style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }}>
                        <Space wrap>
                            <Select
                                placeholder="User"
                                style={{ width: 160 }}
                                allowClear
                                showSearch
                                options={userOptions}
                                value={selectedUserId ?? undefined}
                                onChange={handleUserChange}
                                filterOption={(input, option) =>
                                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                            <RangePicker
                                value={dateRange}
                                onChange={handleDateChange}
                                format="YYYY-MM-DD"
                                style={{ width: 240 }}
                            />
                            <Input.Search
                                placeholder="Search by username, module or action..."
                                allowClear
                                style={{ width: 300 }}
                                value={searchInput}
                                onChange={handleSearchChange}
                                onClear={handleSearchClear}
                            />
                        </Space>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={generatePDF}
                            disabled={total === 0 || isGeneratingPDF}
                            loading={isGeneratingPDF}
                        >
                            Export Data
                        </Button>
                    </Space>

                    <Table
                        columns={columns}
                        rowKey="id"
                        dataSource={tableData}
                        loading={Audit_Red.loading}
                        onChange={handleTableChange}
                        pagination={{
                            current: page + 1,
                            pageSize: pageSize,
                            total,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "15", "20", "50"],
                            showTotal: (tot) => `Total ${tot} records`,
                        }}
                    />
                </Card>
            </div>
        </>
    );
}

function mapStateToProps(state: any) {
    return {
        Audit_Red: state.Audit_Red,
        User_Red: state.User_Red
    }
}

export default connect(mapStateToProps, { ...Action, ...UserAction })(UserActivityAuditLog)
